import {
  DropdownSubmitButton,
  Form,
  NumericFormatField,
  SelectField,
  SubmitButton,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import { MAX_PAYMENTS_WITH_COMMA } from '@/features/project/_utils/constants';
import {
  PaymentGroupFormSchema,
  displayCode,
} from '@/features/project/projects/_models/payment-group';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';

type FormValues = z.infer<typeof PaymentGroupFormSchema>;
type CreatePaymentGroupNextDrawerKey = 'payment-group' | 'payee-payment';

type Props = {
  initialValues?: Partial<FormValues>;
  onSubmit: ({
    values,
    nextDrawerKey,
    onSuccess,
  }: {
    values: FormValues;
    nextDrawerKey?: CreatePaymentGroupNextDrawerKey;
    onSuccess?: () => void;
  }) => Promise<void>;
  usedDisplayCodes: string[];
};

export const PaymentGroupForm: FC<Props> = ({
  initialValues,
  onSubmit,
  usedDisplayCodes,
}) => {
  const { t } = useTranslation();
  const { drawerState } = useProjectDetailDrawerState();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(PaymentGroupFormSchema),
  });

  const isEditing =
    drawerState.open &&
    drawerState.type === 'payment-group' &&
    drawerState.isEditing;

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit((values) => onSubmit({ values }))}
    >
      <SelectField
        name="displayCode"
        label={t('payment-display-code')}
        hint={t('hint-payment-display-code')}
        control={control}
        errors={errors}
        options={displayCode.options.flatMap((code) =>
          !usedDisplayCodes.includes(code) ||
          code === initialValues?.displayCode
            ? {
                value: code,
                label: code,
              }
            : [],
        )}
        required
      />

      <TextInputField
        name="name"
        autocompleteKey="paymentGroup.name"
        label={t('payment-group-name')}
        hint={t('hint-payment-group-name')}
        control={control}
        errors={errors}
        required
      />
      <NumericFormatField
        name="paymentCount"
        label={t('payment-count')}
        hint={t('hint-payment-count')}
        control={control}
        errors={errors}
        allowNegative={false}
        maxLength={MAX_PAYMENTS_WITH_COMMA}
        required
      />
      <TextAreaInputField
        name="overview"
        label={t('payment-group-overview')}
        hint={t('hint-payment-overview')}
        control={control}
        errors={errors}
        rows={3}
        required
      />
      {isEditing ? (
        <SubmitButton loading={isSubmitting} disabled={!isDirty} />
      ) : (
        <DropdownSubmitButton<CreatePaymentGroupNextDrawerKey>
          loading={isSubmitting}
          menuItems={[
            {
              key: 'payee-payment',
              label: t('save-and-create-item', {
                item: t('payment'),
              }),
            },
            {
              key: 'payment-group',
              label: t('save-and-create-next-item', {
                item: t('payment-group'),
              }),
            },
          ]}
          onClick={(e) => {
            handleSubmit(async (values) => {
              await onSubmit({
                values,
                nextDrawerKey: e.key,
                onSuccess: () => {
                  if (e.key === 'payment-group') {
                    reset(initialValues);
                  }
                },
              });
            })();
          }}
        />
      )}
    </Form>
  );
};
